import React from "react";
import { User } from "../../../types/User";
import {
  CaretUpOutlined, CaretDownOutlined,
  EditOutlined, DeleteOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
  LeftOutlined, RightOutlined,
  SearchOutlined,
} from "@ant-design/icons";

interface UserListProps {
  users: User[];
  onEdit: (user: User) => void;
  onDelete: (userId: string) => void;
  loading?: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  sortField: keyof User;
  sortDirection: "asc" | "desc";
  handleSort: (field: keyof User) => void;
  currentPage: number;
  totalPages: number;
  totalUsers: number;
  itemsPerPage: number;
  setCurrentPage: (page: number) => void;
  clearFilters: () => void;
}

const UserList: React.FC<UserListProps> = ({
  users, onEdit, onDelete, loading = false,
  searchTerm, setSearchTerm,
  sortField, sortDirection, handleSort,
  currentPage, totalPages, totalUsers, itemsPerPage,
  setCurrentPage, clearFilters,
}) => {

  const formatDate = (date?: Date | string | null) => {
    if (!date) return "-";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "-";
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
    }).format(d);
  };

  const SortIcon = ({ field }: { field: keyof User }) => {
    if (sortField !== field) return <CaretUpOutlined className="ml-1 text-[10px] opacity-30" />;
    return sortDirection === "asc"
      ? <CaretUpOutlined  className="ml-1 text-[10px] text-blue-500" />
      : <CaretDownOutlined className="ml-1 text-[10px] text-blue-500" />;
  };

  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex   = Math.min(startIndex + itemsPerPage - 1, totalUsers);

  const renderPagination = () => {
    const startPage = Math.max(1, currentPage - 2);
    const endPage   = Math.min(totalPages, currentPage + 2);
    const pages = [];

    const btnBase = `flex items-center justify-center min-w-[36px] h-9 px-3
                     rounded-xl text-sm font-medium border cursor-pointer
                     transition-all duration-200`;

    pages.push(
      <button key="prev"
        onClick={() => setCurrentPage(currentPage - 1)}
        disabled={currentPage === 1}
        className={`${btnBase} border-slate-200 bg-white text-slate-600
                    hover:bg-blue-500 hover:text-white hover:border-blue-500
                    disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white
                    disabled:hover:text-slate-600 disabled:hover:border-slate-200`}
      >
        <LeftOutlined style={{ fontSize: 11 }} />
      </button>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button key={i}
          onClick={() => setCurrentPage(i)}
          className={`${btnBase} ${i === currentPage
            ? "bg-gradient-to-r from-blue-500 to-blue-400 text-white border-blue-500 shadow-md shadow-blue-200"
            : "border-slate-200 bg-white text-slate-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600"
          }`}
        >
          {i}
        </button>
      );
    }

    pages.push(
      <button key="next"
        onClick={() => setCurrentPage(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`${btnBase} border-slate-200 bg-white text-slate-600
                    hover:bg-blue-500 hover:text-white hover:border-blue-500
                    disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white
                    disabled:hover:text-slate-600 disabled:hover:border-slate-200`}
      >
        <RightOutlined style={{ fontSize: 11 }} />
      </button>
    );

    return pages;
  };

   if (loading) return (
    <div className="flex flex-col items-center justify-center h-40 gap-3 text-slate-400">
      <div className="h-9 w-9 rounded-full border-[3px] border-blue-100
                      border-t-blue-500 animate-spin" />
      <span className="text-sm font-medium">Chargement...</span>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden mb-6">

       <div className="flex flex-wrap items-center gap-3 px-5 py-4
                      bg-slate-50 border-b border-blue-50">
         <div className="relative flex-1 min-w-[220px] max-w-sm">
          <SearchOutlined className="absolute left-3 top-1/2 -translate-y-1/2
                                     text-blue-300 text-sm pointer-events-none" />
          <input
            type="text"
            placeholder="Rechercher par nom ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm
                       border border-blue-200 bg-white text-blue-900
                       placeholder-blue-300
                       hover:border-blue-400
                       focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100
                       transition-all duration-200"
          />
        </div>

         {searchTerm && (
          <button
            onClick={clearFilters}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl
                       text-sm font-medium text-rose-400 cursor-pointer
                       border border-dashed border-rose-200 bg-transparent
                       hover:border-rose-400 hover:border-solid hover:text-rose-500 hover:bg-rose-50
                       hover:-translate-y-0.5 transition-all duration-200"
          >
            <span>✕</span> Effacer filtres
          </button>
        )}

         <span className="ml-auto text-xs text-slate-400 font-medium">
          {startIndex}–{endIndex} sur {totalUsers} utilisateurs
        </span>
      </div>

       {users.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 gap-3 text-slate-400">
          <span className="text-4xl opacity-30">👥</span>
          <p className="text-sm font-medium">Aucun utilisateur trouvé</p>
          {searchTerm && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-white
                         bg-blue-500 hover:bg-blue-600 border-none cursor-pointer
                         transition-all duration-200"
            >
              Effacer les filtres
            </button>
          )}
        </div>
      ) : (
        <>
           <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-blue-50">
                  {[
                    { label: "Avatar",  field: null },
                    { label: "Nom",     field: "nom" as keyof User },
                    { label: "Email",   field: "email" as keyof User },
                    { label: "Vérifié", field: null },
                    { label: "Créé le", field: "createdAt" as keyof User },
                    { label: "Actions", field: null },
                  ].map(({ label, field }) => (
                    <th
                      key={label}
                      onClick={field ? () => handleSort(field) : undefined}
                      className={`px-4 py-3.5 text-left text-xs font-semibold
                                  uppercase tracking-wide text-slate-500
                                  ${field ? "cursor-pointer hover:text-blue-500 select-none" : ""}`}
                    >
                      {label}
                      {field && <SortIcon field={field} />}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-50">
                {users.map((user) => (
                  <tr key={user.id}
                      className="bg-white hover:bg-blue-50/40 transition-colors duration-150">

                     <td className="px-4 py-3.5">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center
                                      text-sm font-bold text-white
                                      bg-gradient-to-br from-blue-500 to-blue-400
                                      shadow-sm shadow-blue-200">
                        {user.nom?.charAt(0).toUpperCase() || "?"}
                      </div>
                    </td>

                     <td className="px-4 py-3.5">
                      <span className="text-sm font-semibold text-blue-950">
                        {user.nom}
                      </span>
                    </td>

                     <td className="px-4 py-3.5">
                      <span className="text-sm text-slate-500">{user.email}</span>
                    </td>

                     <td className="px-4 py-3.5">
                      {user.isVerified ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1
                                         rounded-full text-xs font-semibold
                                         bg-green-50 text-green-700 border border-green-200">
                          <CheckCircleOutlined style={{ fontSize: 11 }} />
                          Vérifié
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1
                                         rounded-full text-xs font-semibold
                                         bg-rose-50 text-rose-500 border border-rose-200">
                          <CloseCircleOutlined style={{ fontSize: 11 }} />
                          Non vérifié
                        </span>
                      )}
                    </td>

                     <td className="px-4 py-3.5">
                      <span className="text-sm text-slate-500">
                        {formatDate(user.createdAt)}
                      </span>
                    </td>

                     <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => onEdit(user)}
                          title="Modifier"
                          className="w-8 h-8 flex items-center justify-center rounded-xl
                                     bg-orange-50 border border-orange-100 text-orange-500
                                     hover:bg-orange-100 hover:text-orange-600
                                     transition-all duration-200 cursor-pointer"
                        >
                          <EditOutlined style={{ fontSize: 13 }} />
                        </button>
                        <button
                          onClick={() => onDelete(user.id.toString())}
                          title="Supprimer"
                          className="w-8 h-8 flex items-center justify-center rounded-xl
                                     bg-rose-50 border border-rose-100 text-rose-400
                                     hover:bg-rose-100 hover:text-rose-600
                                     transition-all duration-200 cursor-pointer"
                        >
                          <DeleteOutlined style={{ fontSize: 13 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

           {totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-4
                            px-5 py-4 bg-slate-50 border-t border-blue-50">
              <span className="text-xs text-slate-400 font-medium">
                Affichage de {startIndex} à {endIndex} sur {totalUsers} entrées
              </span>
              <div className="flex items-center gap-1.5">
                {renderPagination()}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserList;